// Phase 2c landing — decisions inbox + steering board come in real.
//
// The two read surfaces between the header and the foot now render
// against the issues + decisions returned by Phase 1's HTTP routes. The
// fields-only IssueEditor lands here too: "New request" + the edit
// pencil PATCH/POST through the raw routes. Phase 3a wires the chat
// drawer + the Conversations + Project-activity accordions at the foot;
// Phase 3b makes reorder write and surfaces the Apply/Reorder seams.

import { useCallback, useEffect, useMemo, useState, type JSX } from "react";

import type {
  ApplyMappingPayload,
  DashboardIssue,
  DecomposeChild,
  Decision,
} from "@loom/shared/dashboard";

import { useT } from "../i18n/index.js";

import { ActivityAccordion } from "./components/ActivityAccordion";
import { ChatDrawer } from "./components/ChatDrawer";
import { ConversationsAccordion } from "./components/ConversationsAccordion";
import { DashFoot } from "./components/DashFoot";
import {
  DecisionsInbox,
  type DecisionActionVerb,
} from "./components/DecisionsInbox";
import { DashHeader } from "./components/DashHeader";
import { Icon } from "./components/Icon";
import { IssueSearch } from "./components/IssueSearch";
import {
  IssueEditor,
  type IssueEditorFocusField,
  type IssueEditorTab,
} from "./components/IssueEditor";
import { Roadmap } from "./components/Roadmap";
import { SetupBanner } from "./components/SetupBanner";
import { SteerBoard, type SteerFilter } from "./components/SteerBoard";
import { TopBar } from "./components/TopBar";
import { useApplyMapping } from "./useApplyMapping";
import {
  useDashboardConfig,
  useDashboardData,
  useProjectLabels,
} from "./useDashboard";
import { useDashboardEvents } from "./useDashboardEvents";
import { useDecompose } from "./useDecompose";
import { useMilestones } from "./useMilestones";
import { useProjectIssues } from "./useProjectIssues";
import { useProjectMeta } from "./useProjectMeta";
import { createSession } from "../projects/useProjects";

interface DashboardPageProps {
  readonly projectId: number;
  readonly onBackToPicker: () => void;
  /** Row click + decisions "add_detail" navigate to the per-issue page. */
  readonly onNavigateToIssue: (iid: number) => void;
  /** "New request" entry points navigate to the `/issues/new` create page. */
  readonly onNewRequest: () => void;
}

interface Toast {
  readonly id: number;
  readonly message: string;
}

const useToast = (): {
  toast: Toast | null;
  push: (message: string) => void;
} => {
  const [toast, setToast] = useState<Toast | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(t);
  }, [toast]);
  return {
    toast,
    push: (message) => setToast({ id: Date.now(), message }),
  };
};

type EditingState =
  | { readonly kind: "new" }
  | {
      readonly kind: "edit";
      readonly issue: DashboardIssue;
      readonly focusField?: IssueEditorFocusField;
      readonly initialTab?: IssueEditorTab;
      readonly childrenProposals?: ReadonlyArray<DecomposeChild>;
    }
  | null;

/**
 * Map a DoR-fail `missing` string (free-form `dorReason`) onto an editor
 * focus field. The plan is explicit that hard-coding "focus AC" would be
 * wrong — DoR can fail on any of title / outcome / acceptance criteria.
 */
const focusFromMissing = (
  missing: string | undefined,
): IssueEditorFocusField => {
  if (!missing) return "title";
  const m = missing.toLowerCase();
  if (/acceptance|criteria|gherkin/.test(m)) return "acceptance_criteria";
  if (/outcome|business/.test(m)) return "outcome";
  return "title";
};

export const DashboardPage = ({
  projectId,
  onBackToPicker,
  onNavigateToIssue,
  onNewRequest,
}: DashboardPageProps): JSX.Element => {
  const t = useT();
  const meta = useProjectMeta(projectId);
  const config = useDashboardConfig(projectId);
  const data = useDashboardData(projectId);
  const projectLabels = useProjectLabels(projectId);
  const projectIssues = useProjectIssues(projectId);
  const applyMapping = useApplyMapping(projectId);
  const decompose = useDecompose(projectId);
  const milestones = useMilestones(projectId);
  const { toast, push } = useToast();
  const [filter, setFilter] = useState<SteerFilter>("active");
  const [editing, setEditing] = useState<EditingState>(null);

  // Phase 3b — single SSE channel; debounced fan-out to every dashboard
  // hook. The accordions are lazy-loaded (re-fetch on expand) and stay
  // out of the invalidation set for now.
  const refetchers = useMemo(
    () => [
      data.refetch,
      config.refetch,
      projectIssues.refetch,
      milestones.refetch,
    ],
    [data.refetch, config.refetch, projectIssues.refetch, milestones.refetch],
  );
  useDashboardEvents(projectId, { refetch: refetchers });

  // Phase 3a — the dashboard owns the chat drawer. Open state + seed +
  // active session are kept here so closing the drawer is CSS-only and
  // the inner ChatPage's WebSocket persists across reopens. Project
  // navigation unmounts DashboardPage (the router supplies a different
  // projectId) so a project switch tears the drawer down for free.
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState<string | null>(null);
  const [chatSeedMode, setChatSeedMode] = useState<"draft" | "send">("draft");
  const [chatActiveSessionId, setChatActiveSessionId] = useState<string | null>(
    null,
  );
  // Tracks whether a pre-warm createSession is already in flight so we
  // don't fire it twice when the user opens the New Request form, closes
  // it, and reopens before the first POST /sessions returns.
  const [chatPrewarming, setChatPrewarming] = useState(false);

  const openChat = useCallback((seed?: string): void => {
    if (seed !== undefined) setChatSeed(seed);
    setChatSeedMode("draft");
    setChatOpen(true);
  }, []);

  // "Chat with Loom about this" — from a steering-board row or the top
  // search bar. Seeds the drawer in draft mode with a short opener naming
  // the issue, so the user lands in the composer ready to ask their
  // question rather than re-typing which request they mean.
  const chatAboutIssue = useCallback(
    (issue: DashboardIssue): void => {
      openChat(`About ${issue.key} "${issue.title}": `);
    },
    [openChat],
  );

  // ⌘K toggles the chat drawer; ⌘N opens the new-request editor. Both
  // honour the design's button labels — the inner ChatPage owns
  // Esc-while-typing and IssueEditor owns Esc-while-open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === "k") {
        e.preventDefault();
        setChatOpen((o) => !o);
        return;
      }
      if (e.key === "n") {
        // Don't hijack ⌘N when the user is editing — typing 'n' while
        // they're in a textarea/input would otherwise pop the modal.
        const target = e.target as Element | null;
        const tag = target?.tagName ?? "";
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          (target instanceof HTMLElement && target.isContentEditable)
        ) {
          return;
        }
        e.preventDefault();
        onNewRequest();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNewRequest]);

  const openEditorWithProposals = useCallback(
    (
      iid: number,
      children: ReadonlyArray<DecomposeChild>,
    ): void => {
      const found = projectIssues.issues.find((i) => i.iid === iid);
      if (!found) {
        push(t("dashboard.toast.notFoundIid", { iid }));
        return;
      }
      setEditing({
        kind: "edit",
        issue: found,
        initialTab: "proposals",
        childrenProposals: children,
      });
    },
    [projectIssues.issues, push, t],
  );

  const runSplit = useCallback(
    async (iid: number): Promise<void> => {
      try {
        const result = await decompose.run(iid);
        if (result.methodologySkipped) {
          push(t("dashboard.toast.decomposeSkipped"));
          return;
        }
        openEditorWithProposals(iid, result.children);
      } catch (err) {
        push(
          t("dashboard.toast.decomposeFailed", {
            error: err instanceof Error ? err.message : String(err),
          }),
        );
      }
    },
    [decompose, openEditorWithProposals, push, t],
  );

  const onDecisionAction = useCallback(
    (decision: Decision, action: DecisionActionVerb): void => {
      switch (action) {
        case "add_detail": {
          // focusFromMissing is no longer used directly — the per-issue
          // page surfaces the DoR-fail reason via the header banner, and
          // the in-page Edit button reopens the editor. Keeping the
          // helper around in case the page later accepts ?focus= in its
          // search params.
          void focusFromMissing;
          if (decision.iid !== undefined) {
            onNavigateToIssue(decision.iid);
          } else {
            onNewRequest();
          }
          return;
        }
        case "answer": {
          const seed = `About ${decision.ref}: ${decision.title}\n\n${decision.body}`;
          openChat(seed);
          return;
        }
        case "review": {
          // Open the editor for the decision's issue, jumping to the
          // proposals view if the issue has any pending proposals
          // (decompose-mode children render via `childrenProposals`;
          // intake link proposals would arrive on a future seam).
          if (decision.iid === undefined) {
            const seed = `About ${decision.ref}: ${decision.title}\n\n${decision.body}`;
            openChat(seed);
            return;
          }
          const found = projectIssues.issues.find(
            (i) => i.iid === decision.iid,
          );
          if (!found) {
            push(t("dashboard.toast.notFoundRef", { ref: decision.ref }));
            return;
          }
          setEditing({
            kind: "edit",
            issue: found,
            initialTab: "proposals",
          });
          return;
        }
        case "split_it": {
          if (decision.iid === undefined) {
            push(t("dashboard.toast.cantDecompose"));
            return;
          }
          void runSplit(decision.iid);
          return;
        }
      }
    },
    [
      onNavigateToIssue,
      openChat,
      onNewRequest,
      projectIssues.issues,
      push,
      runSplit,
      t,
    ],
  );

  const onApplyMapping = useCallback(
    async (payload: ApplyMappingPayload): Promise<void> => {
      try {
        await applyMapping.apply(payload);
        config.refetch();
      } catch {
        // Error surface lives on `applyMapping.error`; the SetupBanner
        // renders the concurrent-write branch off that. Swallow here so
        // we don't double-toast.
      }
    },
    [applyMapping, config],
  );

  // Pre-warm the chat session the moment the user opens a New Request
  // form. The form takes 30-90s for a thoughtful user to fill; cold-
  // starting the sandbox in parallel means the refinement chat opens
  // ready (or at worst, mid-boot with the servant taking the first
  // turn) instead of making the user wait at submit time. If the user
  // cancels the form, the spun-up session lingers under the 60-min idle
  // sweep — it just becomes the next chat session when they reopen.
  useEffect(() => {
    if (editing?.kind !== "new") return;
    if (chatActiveSessionId !== null) return;
    if (chatPrewarming) return;
    setChatPrewarming(true);
    createSession(projectId)
      .then(({ sessionId }) => {
        setChatActiveSessionId(sessionId);
      })
      .catch(() => {
        // Pre-warm is best-effort. If it fails, the drawer's own
        // create-on-open path catches it; the user just sees the boot
        // delay they would have seen pre-fix.
      })
      .finally(() => setChatPrewarming(false));
  }, [editing, chatActiveSessionId, chatPrewarming, projectId]);

  // The modal now only edits existing issues (creation lives on the
  // `/issues/new` page). `onSaved` reconciles the edited row and refreshes
  // the decisions inbox; the post-create bounce + milestone nudge moved
  // off this path — a newly-created issue lands the user straight on its
  // page, where the refinement chat is one tab away.
  const onSaved = useCallback(
    (next: DashboardIssue): void => {
      const list = projectIssues.issues;
      const existing = list.findIndex((i) => i.iid === next.iid);
      if (existing >= 0) {
        const copy = list.slice();
        copy[existing] = next;
        projectIssues.setIssues(copy);
      } else {
        projectIssues.setIssues([next, ...list]);
      }
      setEditing(null);
      // Decisions are derived server-side from issues + labels; refetch so
      // the inbox reflects the new DoR / decision-label state.
      data.refetch();
    },
    [projectIssues, data],
  );

  // First-load error: when we have no data yet and the fetch failed,
  // render the error in place of the dashboard so the user isn't left
  // staring at empty skeletons forever. Once we have any data, transient
  // refetch errors don't replace the page — the SSE invalidation channel
  // re-fires often, and one network blip shouldn't blow the page away.
  if (!data.data && data.error) {
    return (
      <div className="loom-dashboard">
        <div className="lpage">
          <p className="form-error">{data.error}</p>
        </div>
      </div>
    );
  }

  const summary = data.data?.summary ?? null;
  const decisions = data.data?.decisions ?? null;

  return (
    <div className="loom-dashboard">
      <TopBar
        project={meta.data}
        health={summary?.health ?? null}
        onSwitchProject={onBackToPicker}
      />
      <div className="lpage">
        {config.data && !config.data.configured && (
          <SetupBanner
            config={config.data}
            saving={applyMapping.saving}
            error={
              applyMapping.error?.kind === "concurrent_write"
                ? "concurrent_write"
                : (applyMapping.error?.message ?? null)
            }
            availableLabels={projectLabels.data?.labels ?? []}
            onApply={onApplyMapping}
            onRefresh={config.refetch}
          />
        )}

        <DashHeader
          summary={summary}
          onAsk={() => openChat()}
          onNewRequest={onNewRequest}
        />

        <IssueSearch
          issues={projectIssues.issues}
          onOpen={(issue) => onNavigateToIssue(issue.iid)}
        />

        <DecisionsInbox
          decisions={decisions}
          loading={data.loading}
          onDecisionAction={onDecisionAction}
        />

        <SteerBoard
          issues={projectIssues.issues}
          loading={projectIssues.loading}
          filter={filter}
          onFilterChange={setFilter}
          onNew={onNewRequest}
          onEdit={(issue) => onNavigateToIssue(issue.iid)}
          onChat={chatAboutIssue}
        />

        <Roadmap
          rows={milestones.data?.milestones ?? []}
          loading={milestones.loading}
          creating={milestones.creating}
          onCreate={milestones.create}
        />

        <DashFoot>
          <ConversationsAccordion
            projectId={projectId}
            onOpenThread={(sessionId) => {
              setChatActiveSessionId(sessionId);
              setChatOpen(true);
            }}
          />
          <ActivityAccordion projectId={projectId} />
        </DashFoot>
      </div>

      {!chatOpen && (
        <button
          type="button"
          className="chat-fab"
          onClick={() => openChat()}
          aria-label={t("dashboard.fab.openChat")}
        >
          <Icon name="chat" size={14} />
          <span>{t("dashboard.fab.askLoom")}</span>
          <span className="kbd">⌘K</span>
        </button>
      )}

      <ChatDrawer
        projectId={projectId}
        open={chatOpen}
        seed={chatSeed}
        onSeedConsumed={() => setChatSeed(null)}
        seedMode={chatSeedMode}
        onClose={() => setChatOpen(false)}
        activeSessionId={chatActiveSessionId}
        setActiveSessionId={setChatActiveSessionId}
        prewarming={chatPrewarming}
        onBackToPicker={onBackToPicker}
      />

      {editing && (
        <IssueEditor
          projectId={projectId}
          issue={editing.kind === "new" ? null : editing.issue}
          {...(editing.kind === "edit" && editing.focusField !== undefined
            ? { focusField: editing.focusField }
            : {})}
          {...(editing.kind === "edit" && editing.initialTab !== undefined
            ? { initialTab: editing.initialTab }
            : {})}
          {...(editing.kind === "edit" && editing.childrenProposals !== undefined
            ? { childrenProposals: editing.childrenProposals }
            : {})}
          {...(editing.kind === "new"
            ? { intakeBranch: { draftId: `draft-${editing.kind}` } }
            : {})}
          milestones={(milestones.data?.milestones ?? []).map((r) => r.milestone)}
          onAssignMilestone={async (iid, milestoneId) => {
            await milestones.assignToIssue(iid, milestoneId);
            projectIssues.refetch();
          }}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      {toast && (
        <div role="status" aria-live="polite" className="dash-toast">
          {toast.message}
        </div>
      )}
    </div>
  );
};
